import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Combobox,
  Container,
  Group,
  InputBase,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  useCombobox,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconDotsVertical, IconPlus, IconSearch, IconTrash, IconUserCheck, IconUserOff } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import type { ApiData } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { graphql } from '@/lib/graphql';

const PlatformUsersSubscription = graphql(`
  subscription PlatformUsers {
    users(orderBy: { email: ASC }) {
      id
      email
      firstName
      lastName
      createdAt
    }
  }
`);

type SuperUser = ApiData<(typeof api)['super-users']['get']>[number];

type PlatformUser = { id: string; email: string; name: string; createdAt: string };

type SuperUserList = { superUsers: SuperUser[] | null; error: string | null };

async function fetchSuperUsers() {
  const result = await api['super-users'].get();

  if (result.error) {
    return { superUsers: null, error: getErrorMessage(result.error, 'Failed to load super users') };
  }

  return { superUsers: result.data, error: null };
}

function fullName(firstName: string | null, lastName: string | null) {
  return [firstName, lastName].filter(Boolean).join(' ');
}

export const Route = createFileRoute('/_authed/system/super-users')({
  component: SystemSuperUsersPage,
});

function SuperUserRow({
  superUser,
  isSelf,
  onRemove,
}: {
  superUser: SuperUser;
  isSelf: boolean;
  onRemove: (superUser: SuperUser) => void;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          {superUser.email}
          {isSelf && (
            <Badge size="xs" variant="light">
              You
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {fullName(superUser.firstName, superUser.lastName) || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <ActionIcon variant="subtle" color="red" onClick={() => onRemove(superUser)} disabled={isSelf}>
          <IconTrash size={16} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}

function PlatformUserRow({
  user,
  isSuperUser,
  onDeactivate,
  onReactivate,
}: {
  user: PlatformUser;
  isSuperUser: boolean;
  onDeactivate: (user: PlatformUser) => void;
  onReactivate: (user: PlatformUser) => void;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          {user.email}
          {isSuperUser && (
            <Badge size="xs" color="grape" variant="light">
              Super user
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {user.name || '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {new Date(user.createdAt).toLocaleDateString()}
        </Text>
      </Table.Td>
      <Table.Td>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconUserCheck size={14} />} onClick={() => onReactivate(user)}>
              Reactivate sign-in
            </Menu.Item>
            <Menu.Item color="red" leftSection={<IconUserOff size={14} />} onClick={() => onDeactivate(user)}>
              Deactivate sign-in
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  );
}

function AddSuperUserModal({
  opened,
  candidates,
  onClose,
  onAdded,
}: {
  opened: boolean;
  candidates: PlatformUser[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [selected, setSelected] = useState<PlatformUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = useMemo(() => {
    if (debouncedSearch.length < 2) return [];

    const needle = debouncedSearch.toLowerCase();

    return candidates
      .filter((u) => u.email.toLowerCase().includes(needle) || u.name.toLowerCase().includes(needle))
      .slice(0, 10);
  }, [candidates, debouncedSearch]);

  const close = () => {
    setSearch('');
    setSelected(null);
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!selected) return;

    setIsSubmitting(true);
    setError(null);

    const result = await api['super-users'].post({ email: selected.email });

    setIsSubmitting(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to add super user'));

    onAdded();
    close();
  };

  return (
    <Modal opened={opened} onClose={close} title="Add Super User">
      <Stack>
        <ErrorAlert message={error} />
        <Combobox
          store={combobox}
          onOptionSubmit={(value) => {
            const user = matches.find((u) => u.id === value);

            if (user) {
              setSelected(user);
              setSearch(user.name ? `${user.name} (${user.email})` : user.email);
            }

            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <InputBase
              label="Search users"
              placeholder="Type to search by email..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setSelected(null);
                combobox.openDropdown();
              }}
              onFocus={() => {
                if (matches.length > 0) combobox.openDropdown();
              }}
              onBlur={() => combobox.closeDropdown()}
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              {matches.map((user) => (
                <Combobox.Option key={user.id} value={user.id}>
                  <Stack gap={0}>
                    {user.name && (
                      <Text size="sm" fw={500}>
                        {user.name}
                      </Text>
                    )}
                    <Text size="xs" c="dimmed">
                      {user.email}
                    </Text>
                  </Stack>
                </Combobox.Option>
              ))}
              {debouncedSearch.length >= 2 && matches.length === 0 && <Combobox.Empty>No users found</Combobox.Empty>}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={isSubmitting} disabled={!selected}>
            Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function SystemSuperUsersPage() {
  const { me } = useMe();
  const [{ data }] = useSubscription({ query: PlatformUsersSubscription });
  const [list, setList] = useState<SuperUserList>({ superUsers: null, error: null });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removing, setRemoving] = useState<SuperUser | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<PlatformUser | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reloadSuperUsers = useCallback(async () => setList(await fetchSuperUsers()), []);

  useEffect(() => {
    let cancelled = false;

    void fetchSuperUsers().then((next) => {
      if (!cancelled) setList(next);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const platformUsers = useMemo<PlatformUser[]>(
    () =>
      (data?.users ?? []).map((u) => ({
        id: u.id,
        email: u.email,
        name: fullName(u.firstName, u.lastName),
        createdAt: u.createdAt,
      })),
    [data],
  );

  const superUserIds = useMemo(() => new Set((list.superUsers ?? []).map((s) => s.id)), [list.superUsers]);
  const candidates = useMemo(() => platformUsers.filter((u) => !superUserIds.has(u.id)), [platformUsers, superUserIds]);

  const confirmRemove = async () => {
    if (!removing) return;

    setIsSubmitting(true);
    setRemoveError(null);

    const result = await api['super-users']({ userId: removing.id }).delete();

    setIsSubmitting(false);

    if (result.error) return setRemoveError(getErrorMessage(result.error, 'Failed to remove super user'));

    setRemoving(null);
    await reloadSuperUsers();
  };

  const confirmDeactivate = async () => {
    if (!deactivating) return;

    setIsSubmitting(true);
    setDeactivateError(null);

    const result = await api.users({ userId: deactivating.id }).deactivate.post();

    setIsSubmitting(false);

    if (result.error) return setDeactivateError(getErrorMessage(result.error, 'Failed to deactivate user'));

    notifications.show({ color: 'green', message: `${deactivating.email} can no longer sign in` });
    setDeactivating(null);
  };

  const reactivate = async (user: PlatformUser) => {
    const result = await api.users({ userId: user.id }).reactivate.post();

    if (result.error) {
      return notifications.show({
        color: 'red',
        title: 'Unable to reactivate user',
        message: getErrorMessage(result.error, 'Failed to reactivate user'),
      });
    }

    notifications.show({ color: 'green', message: `${user.email} can sign in again` });
  };

  if (data === undefined || (list.superUsers === null && list.error === null)) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const superUsers = list.superUsers ?? [];

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>Super Users</Title>
              <Text c="dimmed" mt="xs">
                Manage system administrators
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setAddModalOpen(true)}>
              Add Super User
            </Button>
          </Group>

          <ErrorAlert message={list.error} />

          <Paper withBorder radius="lg">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {superUsers.map((superUser) => (
                  <SuperUserRow
                    key={superUser.id}
                    superUser={superUser}
                    isSelf={superUser.id === me.id}
                    onRemove={(target) => {
                      setRemoving(target);
                      setRemoveError(null);
                    }}
                  />
                ))}
                {superUsers.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Text c="dimmed" ta="center" py="md">
                        No super users found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>

          <div>
            <Title order={3}>Platform Users</Title>
            <Text c="dimmed" mt="xs" size="sm">
              Every user known to the platform. Deactivating a user blocks their sign-in without deleting their record.
            </Text>
          </div>

          <Paper withBorder radius="lg">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Joined</Table.Th>
                  <Table.Th w={60} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {platformUsers.map((user) => (
                  <PlatformUserRow
                    key={user.id}
                    user={user}
                    isSuperUser={superUserIds.has(user.id)}
                    onDeactivate={(target) => {
                      setDeactivating(target);
                      setDeactivateError(null);
                    }}
                    onReactivate={(target) => void reactivate(target)}
                  />
                ))}
                {platformUsers.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text c="dimmed" ta="center" py="md">
                        No users found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      </Container>

      <AddSuperUserModal
        opened={addModalOpen}
        candidates={candidates}
        onClose={() => setAddModalOpen(false)}
        onAdded={() => void reloadSuperUsers()}
      />

      <Modal opened={removing !== null} onClose={() => setRemoving(null)} title="Remove Super User">
        <Stack>
          <ErrorAlert message={removeError} />
          <Text>Are you sure you want to remove this super user? They will lose all admin privileges.</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void confirmRemove()} loading={isSubmitting}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={deactivating !== null} onClose={() => setDeactivating(null)} title="Deactivate User">
        <Stack>
          <ErrorAlert message={deactivateError} />
          <Text>
            {deactivating?.email} will no longer be able to sign in. Users with active organization memberships cannot
            be deactivated — remove their memberships first.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeactivating(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void confirmDeactivate()} loading={isSubmitting}>
              Deactivate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
